import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Download, BarChart3, DollarSign, Clock, Filter, Package, TrendingUp } from 'lucide-react';
import { getJobs } from '../../../redux/features/jobSlice';
import { getWorkers } from '../../../redux/features/workerSlice';
import { getPartners } from '../../../redux/features/partnerSlice';
import { getWorkerName, getWorkerColor, getWorkerInitials } from '../../../utils/workerUtils';
import { applyAssigneeFilters } from '../../../utils/archiveFilterUtils';
import {
  parseMoneyAmountSR,
  getArchivedJobTimelineDate,
  getArchivedJobDateOnlyForFilter,
  getArchivedJobLocalDayKey
} from '../../../utils/displayUtils';
import DatePickerField from '../../../components/forms/job/DatePickerField';
import { logPeriodJobsTable } from '../../../utils/periodDebugLog';

const localCalendarDayKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Analitika završenih poslova za admin deo aplikacije.
 * Ovaj view je fokusiran na filtere i metrike, bez kalendarskog prikaza.
 */
const JobAnalyticsView = () => {
  const dispatch = useDispatch();
  const { jobs, loading } = useSelector((state) => state.job);
  const { workers } = useSelector((state) => state.worker || { workers: [] });
  const { partners } = useSelector((state) => state.partner || { partners: [] });
  const { user } = useSelector((state) => state.auth);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [warrantyFilter, setWarrantyFilter] = useState('all'); // all | yes | no
  const [fiscalFilter, setFiscalFilter] = useState('all'); // all | yes | no – izdat fiskalni račun
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);

  const [statistics, setStatistics] = useState({
    totalRevenue: 0,
    totalMaterials: 0,
    totalReversiCost: 0,
    totalAdditionalMaterialsCost: 0,
    totalJobs: 0,
    averageJobTime: 0,
    averageTravelTime: 0,
    averageWorkTime: 0,
    totalAmortization: 0,
    totalProfit: 0,
    singleVisit: 0,
    twoVisits: 0,
    threeVisits: 0,
    multipleVisits: 0
  });

  // Helper: build daily/weekly/monthly series for charts
  const buildDailySeries = (jobsList, daysBack = 7) => {
    const today = new Date();
    const result = [];
    for (let i = daysBack - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const key = localCalendarDayKey(d);
      const count = jobsList.filter((job) => {
        const jKey = getArchivedJobLocalDayKey(job);
        return jKey === key;
      }).length;
      result.push({
        label: d.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit' }),
        value: count
      });
    }
    return result;
  };

  const buildWeeklySeries = (jobsList, weeksBack = 4) => {
    const today = new Date();
    const result = [];
    for (let i = weeksBack - 1; i >= 0; i--) {
      const end = new Date(today);
      end.setDate(today.getDate() - i * 7);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      const count = jobsList.filter((job) => {
        const jd = getArchivedJobTimelineDate(job);
        return !Number.isNaN(jd.getTime()) && jd >= start && jd <= end;
      }).length;
      result.push({
        label: `${start.toLocaleDateString('sr-RS', {
          day: '2-digit',
          month: '2-digit'
        })} - ${end.toLocaleDateString('sr-RS', {
          day: '2-digit',
          month: '2-digit'
        })}`,
        value: count
      });
    }
    return result;
  };

  const buildMonthlySeries = (jobsList, monthsBack = 6) => {
    const today = new Date();
    const result = [];
    for (let i = monthsBack - 1; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const month = d.getMonth();
      const year = d.getFullYear();
      const start = new Date(year, month, 1, 0, 0, 0, 0);
      const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
      const count = jobsList.filter((job) => {
        const jd = getArchivedJobTimelineDate(job);
        return !Number.isNaN(jd.getTime()) && jd >= start && jd <= end;
      }).length;
      result.push({
        label: d.toLocaleDateString('sr-RS', { month: 'short', year: '2-digit' }),
        value: count
      });
    }
    return result;
  };

  const buildYearlySeries = (jobsList, yearsBack = 4) => {
    const today = new Date();
    const result = [];
    for (let i = yearsBack - 1; i >= 0; i--) {
      const year = today.getFullYear() - i;
      const start = new Date(year, 0, 1, 0, 0, 0, 0);
      const end = new Date(year, 11, 31, 23, 59, 59, 999);
      const count = jobsList.filter((job) => {
        const jd = getArchivedJobTimelineDate(job);
        return !Number.isNaN(jd.getTime()) && jd >= start && jd <= end;
      }).length;
      result.push({
        label: String(year),
        value: count
      });
    }
    return result;
  };

  const buildDailyTravelTimeSeries = (jobsList, daysBack = 7) => {
    const today = new Date();
    const result = [];
    for (let i = daysBack - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const key = localCalendarDayKey(d);
      let total = 0;
      let count = 0;
      jobsList.forEach((job) => {
        const jKey = getArchivedJobLocalDayKey(job);
        if (jKey === key && job.report) {
          const t = job.report.totalTravelTime || 0;
          total += t;
          count++;
        }
      });
      const avgSeconds = count > 0 ? total / count : 0;
      const avgMinutes = Math.round(avgSeconds / 60);
      result.push({
        label: d.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit' }),
        value: avgMinutes
      });
    }
    return result;
  };

  const buildWeeklyTravelTimeSeries = (jobsList, weeksBack = 4) => {
    const today = new Date();
    const result = [];
    for (let i = weeksBack - 1; i >= 0; i--) {
      const end = new Date(today);
      end.setDate(today.getDate() - i * 7);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      let total = 0;
      let count = 0;
      jobsList.forEach((job) => {
        const jd = getArchivedJobTimelineDate(job);
        if (!Number.isNaN(jd.getTime()) && jd >= start && jd <= end && job.report) {
          total += job.report.totalTravelTime || 0;
          count++;
        }
      });
      const avgMinutes = count > 0 ? Math.round(total / count / 60) : 0;
      result.push({
        label: `${start.toLocaleDateString('sr-RS', {
          day: '2-digit',
          month: '2-digit'
        })} - ${end.toLocaleDateString('sr-RS', {
          day: '2-digit',
          month: '2-digit'
        })}`,
        value: avgMinutes
      });
    }
    return result;
  };

  const buildMonthlyTravelTimeSeries = (jobsList, monthsBack = 6) => {
    const today = new Date();
    const result = [];
    for (let i = monthsBack - 1; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const month = d.getMonth();
      const year = d.getFullYear();
      const start = new Date(year, month, 1, 0, 0, 0, 0);
      const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
      let total = 0;
      let count = 0;
      jobsList.forEach((job) => {
        const jd = getArchivedJobTimelineDate(job);
        if (!Number.isNaN(jd.getTime()) && jd >= start && jd <= end && job.report) {
          const t = job.report.totalTravelTime || 0;
          total += t;
          count++;
        }
      });
      const avgSeconds = count > 0 ? total / count : 0;
      const avgMinutes = Math.round(avgSeconds / 60);
      result.push({
        label: d.toLocaleDateString('sr-RS', { month: 'short', year: '2-digit' }),
        value: avgMinutes
      });
    }
    return result;
  };

  const buildYearlyTravelTimeSeries = (jobsList, yearsBack = 4) => {
    const today = new Date();
    const result = [];
    for (let i = yearsBack - 1; i >= 0; i--) {
      const year = today.getFullYear() - i;
      const start = new Date(year, 0, 1, 0, 0, 0, 0);
      const end = new Date(year, 11, 31, 23, 59, 59, 999);
      let total = 0;
      let count = 0;
      jobsList.forEach((job) => {
        const jd = getArchivedJobTimelineDate(job);
        if (!Number.isNaN(jd.getTime()) && jd >= start && jd <= end && job.report) {
          total += job.report.totalTravelTime || 0;
          count++;
        }
      });
      const avgMinutes = count > 0 ? Math.round(total / count / 60) : 0;
      result.push({
        label: String(year),
        value: avgMinutes
      });
    }
    return result;
  };

  const buildTopGroupsSeries = (jobsList, extractor, limit = 6, fallbackLabel = 'Nepoznato') => {
    const groups = {};
    jobsList.forEach((job) => {
      const raw = extractor(job);
      const label = String(raw || fallbackLabel).trim() || fallbackLabel;
      groups[label] = (groups[label] || 0) + 1;
    });
    return Object.entries(groups)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, limit);
  };

  const buildVisitGroupsSeries = (jobsList) => {
    const buckets = {
      'Prvi dolazak': 0,
      'Drugi dolazak': 0,
      'Treći+ dolazak': 0
    };
    jobsList.forEach((job) => {
      const visits = Number(job.visitCount || 1);
      if (visits <= 1) buckets['Prvi dolazak'] += 1;
      else if (visits === 2) buckets['Drugi dolazak'] += 1;
      else buckets['Treći+ dolazak'] += 1;
    });
    return Object.entries(buckets).map(([label, value]) => ({ label, value }));
  };

  const buildDailyWorkTimeSeries = (jobsList, daysBack = 7) => {
    const today = new Date();
    const result = [];
    for (let i = daysBack - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const key = localCalendarDayKey(d);
      let total = 0;
      let count = 0;
      jobsList.forEach((job) => {
        const jKey = getArchivedJobLocalDayKey(job);
        if (jKey === key && job.report) {
          const t = job.report.totalWorkTime || 0;
          total += t;
          count++;
        }
      });
      const avgMinutes = count > 0 ? Math.round(total / count / 60) : 0;
      result.push({
        label: d.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit' }),
        value: avgMinutes
      });
    }
    return result;
  };

  const buildWeeklyWorkTimeSeries = (jobsList, weeksBack = 4) => {
    const today = new Date();
    const result = [];
    for (let i = weeksBack - 1; i >= 0; i--) {
      const end = new Date(today);
      end.setDate(today.getDate() - i * 7);
      const start = new Date(end);
      start.setDate(end.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      let total = 0;
      let count = 0;
      jobsList.forEach((job) => {
        const jd = getArchivedJobTimelineDate(job);
        if (!Number.isNaN(jd.getTime()) && jd >= start && jd <= end && job.report) {
          total += job.report.totalWorkTime || 0;
          count++;
        }
      });
      const avgMinutes = count > 0 ? Math.round(total / count / 60) : 0;
      result.push({
        label: `${start.toLocaleDateString('sr-RS', {
          day: '2-digit',
          month: '2-digit'
        })} - ${end.toLocaleDateString('sr-RS', {
          day: '2-digit',
          month: '2-digit'
        })}`,
        value: avgMinutes
      });
    }
    return result;
  };

  const buildMonthlyWorkTimeSeries = (jobsList, monthsBack = 6) => {
    const today = new Date();
    const result = [];
    for (let i = monthsBack - 1; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const month = d.getMonth();
      const year = d.getFullYear();
      const start = new Date(year, month, 1, 0, 0, 0, 0);
      const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
      let total = 0;
      let count = 0;
      jobsList.forEach((job) => {
        const jd = getArchivedJobTimelineDate(job);
        if (!Number.isNaN(jd.getTime()) && jd >= start && jd <= end && job.report) {
          total += job.report.totalWorkTime || 0;
          count++;
        }
      });
      const avgMinutes = count > 0 ? Math.round(total / count / 60) : 0;
      result.push({
        label: d.toLocaleDateString('sr-RS', { month: 'short', year: '2-digit' }),
        value: avgMinutes
      });
    }
    return result;
  };

  const buildYearlyWorkTimeSeries = (jobsList, yearsBack = 4) => {
    const today = new Date();
    const result = [];
    for (let i = yearsBack - 1; i >= 0; i--) {
      const year = today.getFullYear() - i;
      const start = new Date(year, 0, 1, 0, 0, 0, 0);
      const end = new Date(year, 11, 31, 23, 59, 59, 999);
      let total = 0;
      let count = 0;
      jobsList.forEach((job) => {
        const jd = getArchivedJobTimelineDate(job);
        if (!Number.isNaN(jd.getTime()) && jd >= start && jd <= end && job.report) {
          total += job.report.totalWorkTime || 0;
          count++;
        }
      });
      const avgMinutes = count > 0 ? Math.round(total / count / 60) : 0;
      result.push({
        label: String(year),
        value: avgMinutes
      });
    }
    return result;
  };

  useEffect(() => {
    const businessType = user?.result?.businessType || 'Home Appliance Technician';
    dispatch(getJobs(businessType));
    dispatch(getWorkers());
    dispatch(getPartners());
  }, [dispatch, user]);

  const currentCompanyId = user?.result?._id || user?.result?.companyId || null;

  // Isti skup kao Arhiva (ArchiveView): završeni poslovi, ne samo status Archived.
  const completedJobs = useMemo(() => {
    const all =
      jobs?.filter(
        (job) =>
          job.status === 'Completed' ||
          job.status === 'Archived' ||
          job.status === 'Finished'
      ) || [];

    // Remove duplicates by _id
    return Array.from(new Map(all.map((job) => [job._id, job])).values());
  }, [jobs]);

  // Izvući listu brendova iz završenih poslova (za filter)
  const availableBrands = useMemo(() => {
    const brandSet = new Set();
    completedJobs.forEach((job) => {
      const brand = (job.deviceBrand || '').trim();
      if (brand) {
        brandSet.add(brand);
      }
    });
    return Array.from(brandSet).sort();
  }, [completedJobs]);

  useEffect(() => {
    let filtered = completedJobs;

    // Date range filter
    if (startDate || endDate) {
      filtered = filtered.filter((job) => {
        const jobDateOnly = getArchivedJobDateOnlyForFilter(job);
        if (!jobDateOnly) return false;

        let matchesStart = true;
        let matchesEnd = true;

        if (startDate) {
          const startDateObj = new Date(startDate + 'T00:00:00');
          const startDateOnly = new Date(
            Date.UTC(
              startDateObj.getFullYear(),
              startDateObj.getMonth(),
              startDateObj.getDate()
            )
          );
          matchesStart = jobDateOnly >= startDateOnly;
        }

        if (endDate) {
          const endDateObj = new Date(endDate + 'T23:59:59');
          const endDateOnly = new Date(
            Date.UTC(
              endDateObj.getFullYear(),
              endDateObj.getMonth(),
              endDateObj.getDate()
            )
          );
          matchesEnd = jobDateOnly <= endDateOnly;
        }

        return matchesStart && matchesEnd;
      });
    }

    // Radnik / partner filter (više kriterijuma)
    filtered = applyAssigneeFilters(
      filtered,
      { workerIds: selectedWorkers, partnerIds: selectedPartners },
      { workers, partners, sourceCompanyId: currentCompanyId }
    );

    // Brand filter
    if (selectedBrand !== 'all') {
      filtered = filtered.filter((job) => {
        const brand = (job.deviceBrand || '').trim();
        return brand === selectedBrand;
      });
    }

    // Warranty filter (oslanjamo se na job.report?.underWarranty ako postoji)
    if (warrantyFilter !== 'all') {
      filtered = filtered.filter((job) => {
        const underWarranty = job.report?.underWarranty;
        if (warrantyFilter === 'yes') {
          return !!underWarranty;
        }
        if (warrantyFilter === 'no') {
          return underWarranty === false || underWarranty === undefined;
        }
        return true;
      });
    }

    // Izdat fiskalni – filter po report.fiscalReceiptNumber (da = ima broj, ne = nema)
    if (fiscalFilter !== 'all') {
      filtered = filtered.filter((job) => {
        const hasFiscal = !!(job.report?.fiscalReceiptNumber && String(job.report.fiscalReceiptNumber).trim());
        if (fiscalFilter === 'yes') return hasFiscal;
        if (fiscalFilter === 'no') return !hasFiscal;
        return true;
      });
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((job) => {
        const clientName = (job.clientName || '').toLowerCase();
        const clientPhone = (job.clientPhone || '').toLowerCase();
        const clientAddress = (job.clientAddress || '').toLowerCase();
        const issueDescription = (job.issueDescription || '').toLowerCase();
        const workerName = getWorkerName(
          job.assignedTo,
          workers,
          partners
        ).toLowerCase();
        const materialsUsed = (job.report?.materialsUsed || '').toLowerCase();
        const serviceCharged = (job.report?.serviceCharged || '').toLowerCase();
        const deviceBrand = (job.deviceBrand || '').toLowerCase();

        return (
          clientName.includes(query) ||
          clientPhone.includes(query) ||
          clientAddress.includes(query) ||
          issueDescription.includes(query) ||
          workerName.includes(query) ||
          materialsUsed.includes(query) ||
          serviceCharged.includes(query) ||
          deviceBrand.includes(query)
        );
      });
    }

    filtered.sort((a, b) => {
      const dateA = getArchivedJobTimelineDate(a).getTime();
      const dateB = getArchivedJobTimelineDate(b).getTime();
      return dateB - dateA;
    });

    setFilteredJobs(filtered);

    const stats = calculateStatistics(filtered);
    setStatistics(stats);

    logPeriodJobsTable('[SpinTasker · Analitika]', filtered, {
      startDate,
      endDate,
      selectedWorkers,
      selectedPartners,
      workers,
      partners,
      extra: {
        skupPoslova: 'samo Archived (kao u metrikama)',
        brend: selectedBrand,
        garancija: warrantyFilter,
        fiskalni: fiscalFilter,
        pretraga: searchQuery || '(prazno)'
      }
    });
  }, [
    completedJobs,
    startDate,
    endDate,
    selectedWorkers,
    selectedPartners,
    selectedBrand,
    warrantyFilter,
    fiscalFilter,
    searchQuery,
    workers,
    partners,
    currentCompanyId
  ]);

  const calculateStatistics = (jobsList) => {
    let totalRevenue = 0;
    let totalJobs = jobsList.length;
    let totalTravelTime = 0;
    let totalWorkTime = 0;
    let totalReversiCost = 0;
    let totalAdditionalMaterialsCost = 0;
    let totalAmortization = 0;

    jobsList.forEach((job) => {
      if (job.report) {
        // Prihod (serviceCharged može biti "138.000" – obavezno SR parsiranje)
        const revenue = parseMoneyAmountSR(job.report.serviceCharged);
        totalRevenue += revenue;

        // Calculate reversiCost - from report.reversiCost or calculate from usedSpareParts
        let reversiCost = job.report.reversiCost || 0;
        if (reversiCost === 0 && job.report.usedSpareParts && Array.isArray(job.report.usedSpareParts)) {
          reversiCost = job.report.usedSpareParts.reduce((sum, part) => {
            const price = typeof part.price === 'number' ? part.price : parseFloat(part.price) || 0;
            const quantity = typeof part.quantity === 'number' ? part.quantity : parseFloat(part.quantity) || 1;
            return sum + (price * quantity);
          }, 0);
        }
        
        // Calculate additionalMaterialsCost - from report or try to parse from additionalMaterials
        let additionalMaterialsCost = job.report.additionalMaterialsCost || 0;
        if (additionalMaterialsCost === 0 && job.report.additionalMaterials && Array.isArray(job.report.additionalMaterials)) {
          additionalMaterialsCost = job.report.additionalMaterials.reduce((sum, material) => {
            const price = typeof material.price === 'number' ? material.price : parseFloat(material.price) || 0;
            return sum + price;
          }, 0);
        }
        
        // Amortization cost
        const amortizationCost = job.report.amortizationCost || 0;
        
        // Akumuliramo troškove odvojeno
        totalReversiCost += reversiCost;
        totalAdditionalMaterialsCost += additionalMaterialsCost;
        totalAmortization += amortizationCost;

        // Vremena
        const travelTime = job.report.totalTravelTime || 0;
        const workTime = job.report.totalWorkTime || 0;
        totalTravelTime += travelTime;
        totalWorkTime += workTime;
      }
    });

    const averageTravelTime =
      totalJobs > 0 ? totalTravelTime / totalJobs : 0;
    const averageWorkTime = totalJobs > 0 ? totalWorkTime / totalJobs : 0;
    
    // Ukupan materijal = reversi + dodatni materijal
    const totalMaterialsCost = totalReversiCost + totalAdditionalMaterialsCost;
    
    // Ukupan trošak = reversi + dodatni materijal + amortizacija
    const totalCost = totalReversiCost + totalAdditionalMaterialsCost + totalAmortization;
    
    // Čista zarada = ukupna naplata - ukupan trošak
    const totalProfit = totalRevenue - totalCost;
    
    console.log('💰 Analitika Stats Calculation:', {
      'Ukupna naplata': `${totalRevenue.toLocaleString('sr-RS')} RSD`,
      'Materijal iz reversa': `${totalReversiCost.toLocaleString('sr-RS')} RSD`,
      'Dodatni materijal': `${totalAdditionalMaterialsCost.toLocaleString('sr-RS')} RSD`,
      'Amortizacija': `${totalAmortization.toLocaleString('sr-RS')} RSD`,
      'Ukupan trošak': `${totalCost.toLocaleString('sr-RS')} RSD`,
      'Čista zarada (naplata - trošak)': `${totalProfit.toLocaleString('sr-RS')} RSD`
    });

    return {
      totalRevenue,
      totalMaterials: totalMaterialsCost,
      totalReversiCost,
      totalAdditionalMaterialsCost,
      totalJobs,
      averageJobTime: averageTravelTime + averageWorkTime,
      averageTravelTime,
      averageWorkTime,
      singleVisit: 0,
      twoVisits: 0,
      threeVisits: 0,
      multipleVisits: 0,
      totalAmortization,
      totalProfit
    };
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;
  };

  // Jednostavan horizontalni bar chart koji reaguje na filtere
  const AnalyticsBarChart = ({ title, unit, data }) => {
    const max = data.reduce((m, item) => Math.max(m, item.value), 0);
    return (
      <div className="bg-gray-900 rounded-lg p-3 sm:p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs sm:text-sm font-semibold text-white">
            {title}
          </h4>
          <span className="text-[10px] text-gray-400">
            Maks: {max} {unit}
          </span>
        </div>
        {data.length === 0 ? (
          <div className="text-xs text-gray-500 text-center py-4">
            Nema podataka za izabrane filtere
          </div>
        ) : (
          <div className="space-y-1.5">
            {data.map((item) => {
              const width = max > 0 ? Math.max((item.value / max) * 100, 5) : 0;
              return (
                <div key={item.label} className="flex items-center gap-2">
                  <div className="w-16 text-[10px] sm:text-[11px] text-gray-400 truncate">
                    {item.label}
                  </div>
                  <div className="flex-1 bg-gray-800 rounded-full h-3 overflow-hidden">
                    <div
                      className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                  <div className="w-10 text-right text-[10px] sm:text-[11px] text-gray-200">
                    {item.value} {unit}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const exportToCSV = () => {
    if (!filteredJobs.length) return;

    const csvData = filteredJobs.map((job) => {
      const evd = getArchivedJobTimelineDate(job);
      return {
      Client: job.clientName,
      Phone: job.clientPhone,
      Address: job.clientAddress,
      Issue: job.issueDescription,
      Worker: getWorkerName(job.assignedTo, workers, partners),
      Brand: job.deviceBrand || '',
      Warranty: job.report?.underWarranty ? 'Da' : 'Ne',
      Completed: Number.isNaN(evd.getTime()) ? '' : evd.toLocaleString('sr-RS'),
      Revenue: job.report?.serviceCharged || '',
      Materials: job.report?.materialsUsed || '',
      TravelTime: formatTime(job.report?.totalTravelTime || 0),
      WorkTime: formatTime(job.report?.totalWorkTime || 0)
    };
    });

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map((row) =>
        Object.values(row)
          .map((val) => `"${String(val).replace(/"/g, '""')}"`)
          .join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `job_analytics_${startDate || 'all'}_${endDate || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="h-full bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-900 p-2 sm:p-4">
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold text-white">
              Analitika poslova
            </h1>
            <p className="text-xs sm:text-sm text-gray-400">
              Pregled završenih poslova, vremena i naplate
            </p>
          </div>
          <button
            onClick={exportToCSV}
            disabled={filteredJobs.length === 0}
            className="flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm sm:text-base"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export CSV</span>
            <span className="sm:hidden">Export</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Filter className="h-4 w-4 text-gray-400" />
            <h3 className="text-base sm:text-lg font-semibold text-white">
              Filteri
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {/* Date Range – react-datepicker ostaje otvoren pri promeni meseca dok korisnik ne izabere datum */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                Od datuma
              </label>
              <DatePickerField
                value={startDate}
                onChange={setStartDate}
                placeholder="dd.mm.yyyy"
                inputClass="w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                Do datuma
              </label>
              <DatePickerField
                value={endDate}
                onChange={setEndDate}
                placeholder="dd.mm.yyyy"
                inputClass="w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Worker Filter — više radnika */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                Radnici
              </label>
              <div className="max-h-36 overflow-y-auto bg-gray-700 rounded-lg border border-gray-600 p-2 space-y-1">
                {workers && workers.length > 0 ? (
                  workers.map((worker) => {
                    const workerId = String(worker._id);
                    const workerName = `${worker.firstName || ''} ${worker.lastName || ''}`.trim();
                    return (
                      <label
                        key={workerId}
                        className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-600 p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={selectedWorkers.includes(workerId)}
                          onChange={(e) => {
                            setSelectedWorkers((prev) =>
                              e.target.checked
                                ? [...prev, workerId]
                                : prev.filter((id) => id !== workerId)
                            );
                          }}
                          className="w-3 h-3"
                        />
                        <span>{workerName || worker.email || 'Nepoznat radnik'}</span>
                      </label>
                    );
                  })
                ) : (
                  <p className="text-xs text-gray-400">Nema radnika</p>
                )}
              </div>
            </div>

            {/* Partner Filter — više partnera */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                Servisni partneri
              </label>
              <div className="max-h-36 overflow-y-auto bg-gray-700 rounded-lg border border-gray-600 p-2 space-y-1">
                {partners && partners.length > 0 ? (
                  partners.map((partner) => {
                    const partnerId = String(partner._id || partner.id);
                    return (
                      <label
                        key={partnerId}
                        className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-600 p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPartners.includes(partnerId)}
                          onChange={(e) => {
                            setSelectedPartners((prev) =>
                              e.target.checked
                                ? [...prev, partnerId]
                                : prev.filter((id) => id !== partnerId)
                            );
                          }}
                          className="w-3 h-3"
                        />
                        <span>{partner.companyName || partner.email || 'Nepoznat partner'}</span>
                      </label>
                    );
                  })
                ) : (
                  <p className="text-xs text-gray-400">Nema partnera</p>
                )}
              </div>
            </div>

            {/* Brand Filter */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                Brend
              </label>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Svi brendovi</option>
                {availableBrands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </div>

            {/* Warranty Filter */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                Garancija
              </label>
              <select
                value={warrantyFilter}
                onChange={(e) => setWarrantyFilter(e.target.value)}
                className="w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Sve</option>
                <option value="yes">Pod garancijom</option>
                <option value="no">Bez garancije</option>
              </select>
            </div>

            {/* Izdat fiskalni */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                Izdat fiskalni
              </label>
              <select
                value={fiscalFilter}
                onChange={(e) => setFiscalFilter(e.target.value)}
                className="w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Sve</option>
                <option value="yes">Da</option>
                <option value="no">Ne</option>
              </select>
            </div>
          </div>
        </div>
        {/* Osnovne metrike (poređane kako želiš) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
          {/* Ukupno poslova */}
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
              <span className="text-xs sm:text-sm font-medium text-gray-300">
                Ukupno poslova
              </span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white">
              {statistics.totalJobs}
            </div>
          </div>

          {/* Ukupna naplata */}
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
              <span className="text-xs sm:text-sm font-medium text-gray-300">
                Ukupna naplata
              </span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white">
              {statistics.totalRevenue.toLocaleString('sr-RS')} RSD
            </div>
          </div>

          {/* Materijal iz reversa */}
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
              <span className="text-xs sm:text-sm font-medium text-gray-300">
                Materijal iz reversa
              </span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white">
              {(statistics.totalReversiCost || 0).toLocaleString('sr-RS')} RSD
            </div>
          </div>

          {/* Materijal (trošak) - dodatni materijal */}
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
              <span className="text-xs sm:text-sm font-medium text-gray-300">
                Materijal (trošak)
              </span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white">
              {(statistics.totalAdditionalMaterialsCost || 0).toLocaleString('sr-RS')} RSD
            </div>
          </div>

          {/* Amortizacija */}
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-orange-400" />
              <span className="text-xs sm:text-sm font-medium text-gray-300">
                Amortizacija
              </span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white">
              {statistics.totalAmortization.toLocaleString('sr-RS')} RSD
            </div>
          </div>

          {/* Čista zarada */}
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
              <span className="text-xs sm:text-sm font-medium text-gray-300">
                Čista zarada
              </span>
            </div>
            <div className={`text-xl sm:text-2xl font-bold ${statistics.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {statistics.totalProfit.toLocaleString('sr-RS')} RSD
            </div>
          </div>

          {/* Prosečno vreme puta */}
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
              <span className="text-xs sm:text-sm font-medium text-gray-300">
                Prosečno vreme puta
              </span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white">
              {formatTime(statistics.averageTravelTime)}
            </div>
          </div>

          {/* Prosečno vreme rada */}
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
              <span className="text-xs sm:text-sm font-medium text-gray-300">
                Prosečno vreme rada
              </span>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white">
              {formatTime(statistics.averageWorkTime)}
            </div>
          </div>
        </div>

        {/* Grafici i poređenja po periodima */}
        <div className="bg-gray-800 rounded-lg p-3 sm:p-4 mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">
            Trend poslova i vremena (dani, nedelje, meseci)
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Broj poslova po danima (poslednjih 7 dana) */}
            <AnalyticsBarChart
              title="Poslovi po danima (poslednjih 7 dana)"
              unit="poslova"
              data={buildDailySeries(filteredJobs, 7)}
            />
            {/* Broj poslova po nedeljama (poslednje 4 nedelje) */}
            <AnalyticsBarChart
              title="Poslovi po nedeljama (poslednje 4 nedelje)"
              unit="poslova"
              data={buildWeeklySeries(filteredJobs, 4)}
            />
            {/* Broj poslova po mesecima (poslednjih 6 meseci) */}
            <AnalyticsBarChart
              title="Poslovi po mesecima (poslednjih 6 meseci)"
              unit="poslova"
              data={buildMonthlySeries(filteredJobs, 6)}
            />
            <AnalyticsBarChart
              title="Poslovi po godinama (poslednje 4 godine)"
              unit="poslova"
              data={buildYearlySeries(filteredJobs, 4)}
            />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">
            Poređenja po radnicima, brendovima, tipu aparata i dolascima
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <AnalyticsBarChart
              title="Poslovi po radnicima"
              unit="poslova"
              data={buildTopGroupsSeries(
                filteredJobs,
                (job) => getWorkerName(job.assignedTo, workers, partners) || 'Nedodeljeno'
              )}
            />
            <AnalyticsBarChart
              title="Poslovi po brendovima"
              unit="poslova"
              data={buildTopGroupsSeries(filteredJobs, (job) => job.deviceBrand, 6, 'Bez brenda')}
            />
            <AnalyticsBarChart
              title="Poslovi po tipu aparata"
              unit="poslova"
              data={buildTopGroupsSeries(filteredJobs, (job) => job.deviceType, 6, 'Bez tipa')}
            />
            <AnalyticsBarChart
              title="Poslovi po broju dolazaka"
              unit="poslova"
              data={buildVisitGroupsSeries(filteredJobs)}
            />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">
            Prosečno vreme puta po periodima
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <AnalyticsBarChart
              title="Prosečno vreme puta po danima (minuta)"
              unit="min"
              data={buildDailyTravelTimeSeries(filteredJobs, 7)}
            />
            <AnalyticsBarChart
              title="Prosečno vreme puta po nedeljama (minuta)"
              unit="min"
              data={buildWeeklyTravelTimeSeries(filteredJobs, 4)}
            />
            <AnalyticsBarChart
              title="Prosečno vreme puta po mesecima (minuta)"
              unit="min"
              data={buildMonthlyTravelTimeSeries(filteredJobs, 6)}
            />
            <AnalyticsBarChart
              title="Prosečno vreme puta po godinama (minuta)"
              unit="min"
              data={buildYearlyTravelTimeSeries(filteredJobs, 4)}
            />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">
            Prosečno vreme rada po periodima
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <AnalyticsBarChart
              title="Prosečno vreme rada po danima (minuta)"
              unit="min"
              data={buildDailyWorkTimeSeries(filteredJobs, 7)}
            />
            <AnalyticsBarChart
              title="Prosečno vreme rada po nedeljama (minuta)"
              unit="min"
              data={buildWeeklyWorkTimeSeries(filteredJobs, 4)}
            />
            <AnalyticsBarChart
              title="Prosečno vreme rada po mesecima (minuta)"
              unit="min"
              data={buildMonthlyWorkTimeSeries(filteredJobs, 6)}
            />
            <AnalyticsBarChart
              title="Prosečno vreme rada po godinama (minuta)"
              unit="min"
              data={buildYearlyWorkTimeSeries(filteredJobs, 4)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobAnalyticsView;

